import React, { useEffect, useState } from "react";
import { CheckCircle2, Calendar, MessageSquare, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Phase 47 — Delivery evidence card.
 *
 * Fetches ``GET /api/shipments/{id}/delivery-evidence`` (auth) and
 * renders the photo + signature + optional notes + delivered_at
 * timestamp. Rendered ONLY once the shipment is in ``entregado``
 * status; callers must gate the mount themselves so we don't fire
 * the endpoint prematurely (it returns 409 for undelivered rows).
 *
 * Consumed by admin detail (AdminEnvios) and the customer tracking
 * page (EnvioConfirmacion). No admin-only fields — same view for both.
 */
export default function DeliveryEvidenceCard({ shipmentId }) {
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let cancel = false;
    async function run() {
      try {
        const { data } = await api.get(`/shipments/${shipmentId}/delivery-evidence`);
        if (!cancel) setState({ status: "ready", data });
      } catch (e) {
        if (cancel) return;
        const detail = e?.response?.data?.detail || "No se pudo cargar la evidencia";
        setState({ status: "error", message: detail });
      }
    }
    run();
    return () => {
      cancel = true;
    };
  }, [shipmentId]);

  if (state.status === "loading") {
    return (
      <div
        data-testid="delivery-evidence-loading"
        className="rounded-2xl border bg-white p-5 flex items-center gap-2 text-sm text-neutral-500"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando evidencia de entrega…
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div
        data-testid="delivery-evidence-error"
        className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
      >
        {state.message}
      </div>
    );
  }

  const e = state.data;
  const photo = e.photo_url
    ? e.photo_url.startsWith("http")
      ? e.photo_url
      : `${process.env.REACT_APP_BACKEND_URL || ""}${e.photo_url}`
    : null;
  const sig = e.signature_b64 ? `data:image/png;base64,${e.signature_b64}` : null;
  const when = e.delivered_at ? new Date(e.delivered_at) : null;
  const whenLabel = when
    ? new Intl.DateTimeFormat("es-ES", {
        dateStyle: "long",
        timeStyle: "short",
      }).format(when)
    : "—";

  return (
    <div
      data-testid="delivery-evidence-card"
      className="rounded-2xl border border-emerald-200 bg-white p-5 space-y-4"
    >
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
        <h3 className="text-lg font-black tracking-tight">Evidencia de entrega</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-1">
            Foto de la entrega
          </div>
          {photo ? (
            <a href={photo} target="_blank" rel="noreferrer">
              <img
                data-testid="delivery-photo"
                src={photo}
                alt="Foto de la entrega"
                className="rounded-xl border w-full max-h-60 object-cover"
              />
            </a>
          ) : (
            <div className="text-sm text-neutral-500">Sin foto</div>
          )}
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-neutral-500 mb-1">
            Firma del destinatario
          </div>
          {sig ? (
            <img
              data-testid="delivery-signature"
              src={sig}
              alt="Firma del destinatario"
              className="rounded-xl border w-full max-h-40 object-contain bg-neutral-50"
            />
          ) : (
            <div className="text-sm text-neutral-500">Sin firma</div>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div
          data-testid="delivery-timestamp"
          className="flex items-center gap-2 text-neutral-700"
        >
          <Calendar className="h-4 w-4 text-neutral-500" />
          <span>{whenLabel}</span>
        </div>
        {e.notes && (
          <div
            data-testid="delivery-notes"
            className="flex items-start gap-2 text-neutral-700"
          >
            <MessageSquare className="h-4 w-4 text-neutral-500 mt-0.5 shrink-0" />
            <span className="whitespace-pre-wrap">{e.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
}
